import type { StorefrontDestination, WebsiteDestinationKind } from '@dukanti/shared';

/**
 * Epic #228 P5 (PD9): the cards of the DestinationEditor. Five primary cards
 * in PRD §7.1 order, then the "More destinations" cards. A card is a UI
 * choice, not a kind: "Sales page" and "Store page" are both `store-page`.
 */
export type DestinationCardId =
  | 'none'
  | 'sales'
  | 'category'
  | 'product'
  | 'external'
  | 'store-page'
  | 'custom-collection'
  | 'contact';

export const PRIMARY_DESTINATION_CARDS = [
  'none',
  'sales',
  'category',
  'product',
  'external',
] as const satisfies readonly DestinationCardId[];

export const MORE_DESTINATION_CARDS = [
  'store-page',
  'custom-collection',
  'contact',
] as const satisfies readonly DestinationCardId[];

/** The kind a card writes. `none` is always offered (PD9: "No link" is authored, never a fallback). */
const CARD_KIND: Readonly<Record<DestinationCardId, WebsiteDestinationKind | 'none'>> = {
  none: 'none',
  sales: 'store-page',
  category: 'category',
  product: 'product',
  external: 'external',
  'store-page': 'store-page',
  'custom-collection': 'custom-collection',
  contact: 'contact',
};

export function isDestinationCardAllowed(
  card: DestinationCardId,
  allowedKinds: readonly WebsiteDestinationKind[],
): boolean {
  const kind = CARD_KIND[card];
  return kind === 'none' || allowedKinds.includes(kind);
}

export interface DestinationCardSets {
  readonly primary: readonly DestinationCardId[];
  readonly more: readonly DestinationCardId[];
}

/** The cards a slot offers, in PD9 order, filtered by its allowed kinds. */
export function destinationCardsFor(
  allowedKinds: readonly WebsiteDestinationKind[],
): DestinationCardSets {
  return {
    primary: PRIMARY_DESTINATION_CARDS.filter((card) =>
      isDestinationCardAllowed(card, allowedKinds),
    ),
    more: MORE_DESTINATION_CARDS.filter((card) => isDestinationCardAllowed(card, allowedKinds)),
  };
}

/** The card a stored value checks. `store-page: sales` is the Sales card; any other page is "Store page". */
export function cardForValue(value: StorefrontDestination): DestinationCardId {
  if (value.kind === 'store-page') return value.page === 'sales' ? 'sales' : 'store-page';
  return value.kind;
}

/** True when the card sits behind "More destinations". */
export function isMoreCard(card: DestinationCardId): boolean {
  return (MORE_DESTINATION_CARDS as readonly DestinationCardId[]).includes(card);
}

/**
 * The value a card writes when the merchant picks it. A pick card (category,
 * product, collection) and the URL card start empty: that value stays in the
 * dialog until it is complete, because the region schema refuses it (RULE 0).
 */
export function valueForCard(card: DestinationCardId): StorefrontDestination {
  switch (card) {
    case 'none':
      return { kind: 'none' };
    case 'sales':
      return { kind: 'store-page', page: 'sales' };
    case 'category':
      return { kind: 'category', categoryId: '' };
    case 'product':
      return { kind: 'product', productId: '' };
    case 'external':
      return { kind: 'external', url: '' };
    case 'store-page':
      return { kind: 'store-page', page: 'catalog' };
    case 'custom-collection':
      return { kind: 'custom-collection', collectionRef: '' };
    case 'contact':
      return { kind: 'contact', method: 'whatsapp' };
  }
}

/** The id a pick destination points at, or '' (nothing picked yet / not a pick kind). */
export function pickedTargetId(value: StorefrontDestination): string {
  if (value.kind === 'category') return value.categoryId;
  if (value.kind === 'product') return value.productId;
  if (value.kind === 'custom-collection') return value.collectionRef;
  return '';
}

/** A pick card with nothing picked yet: the picker is the prompt, not an error. */
export function isIncompletePick(value: StorefrontDestination): boolean {
  return (
    (value.kind === 'category' || value.kind === 'product' || value.kind === 'custom-collection') &&
    pickedTargetId(value) === ''
  );
}

/**
 * Roving focus over the visible cards (one column). Up/Down and the inline
 * arrows move and select; the inline arrows follow the reading direction, so
 * in RTL ArrowLeft is "next". Home/End go to the ends. Null = not a move key.
 */
export function nextCardIndex(
  key: string,
  current: number,
  count: number,
  dir: 'rtl' | 'ltr',
): number | null {
  if (count === 0) return null;
  const forward = dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
  const backward = dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
  if (key === 'ArrowDown' || key === forward) return (current + 1) % count;
  if (key === 'ArrowUp' || key === backward) return (current - 1 + count) % count;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
}
